import React from 'react';
import styles from './Button.module.scss';
import { ReactComponent as CheckIcon } from '@/assets/Icons/check.svg';
import { ReactComponent as ClockIcon } from '@/assets/Icons/clock.svg';
import WebApp from '@twa-dev/sdk';
import { useNotificationStore } from '@/store/NotificationStore';

export type ButtonStatus =
  | 'not_started'
  | 'done'
  | 'claim'
  | 'in_progress'
  | 'check';

interface ButtonProps {
  status: ButtonStatus;
  buttonText?: string;
  onNotStartedClick?: () => void;
  onDoneClick?: () => void;
  onClaimClick?: () => void;
  onInProgressClick?: () => void;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}

export const Button: React.FC<ButtonProps> = ({
  status,
  buttonText,
  onNotStartedClick,
  onDoneClick,
  onClaimClick,
  onInProgressClick,
  onClick,
  disabled = false,
  className = '',
}) => {
  const { setNotification } = useNotificationStore.getState();

  const getButtonText = (status: ButtonStatus): string => {
    // Если передан текст кнопки, используем его
    if (buttonText) {
      return buttonText;
    }

    // Иначе используем стандартные тексты
    switch (status) {
      case 'not_started':
        return 'Выполнить';
      case 'done':
        return 'Выполнено';
        // NOTE: fix here
      case 'claim':
        return 'Выполнено';
      case 'in_progress':
        return 'Проверка';
      case 'check':
        return 'Проверить';
      default:
        return '';
    }
  };

  const handleClick = () => {
    // Блокируем клик для заданий в процессе проверки и завершенных
    if (status === 'in_progress' || status === 'claim') {
      return;
    }

    // Добавляем вибрацию при нажатии
    if (WebApp && WebApp.HapticFeedback) {
      WebApp.HapticFeedback.impactOccurred('medium');
    }

    onClick?.();
    // Используем type assertion для корректной работы с типами
    const currentStatus = status as ButtonStatus;
    switch (currentStatus) {
      case 'not_started':
        onNotStartedClick?.();
        break;
      case 'done':
        onDoneClick?.();
        break;
      case 'claim':
        onClaimClick?.();
        break;
      case 'in_progress':
        onInProgressClick?.();
        break;
      case 'check':
        onInProgressClick?.();
        break;
    }

    // Переводим кнопку в статус 'claim' после завершения задания
    if (currentStatus === 'done') {
      status = 'claim';
    }
  };

  // Определяем, должна ли кнопка быть заблокирована
  const isButtonDisabled =
    disabled || status === 'in_progress' || status === 'claim';

  return (
    <button
      onClick={handleClick}
      disabled={isButtonDisabled}
      className={`${styles.button} ${className}`}
      data-status={status}
    >
      {getButtonText(status)}
      {status === 'claim' && (
        <span className={styles.button__icon}>
          <CheckIcon />
        </span>
      )}
      {status === 'in_progress' && (
        <span className={styles.button__icon}>
          <ClockIcon />
        </span>
      )}
    </button>
  );
};
